import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiDelete, mdiSigma } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { RelationTypeFormFields } from './RelationTypeFormFields'
import { fetchRelationUsage, relationTypesQueryKey, relationUsageQueryKey } from './RelationTypesPage'
import {
    formToPatchRequest,
    relationTypeLabel,
    relationTypeToForm,
    type AssetRelationType,
    type RelationTypeFormState,
} from './relation-type-form'
import { apiClient } from '../../api/client'
import { apiErrorMessage } from '../../lib/apiError'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useLocale } from '../../contexts/LocaleContext'
import { useCan } from '../../contexts/PermissionsContext'

export function RelationTypeDetailPage() {
    const { org, relationTypeUid } = useParams({ strict: false }) as {
        org?: string
        relationTypeUid?: string
    }
    const orgCode = org || 'demo'
    const uid = relationTypeUid || ''
    const { t } = useTranslation('types')
    const { locale } = useLocale()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    // Editing a relation type is `configuration.write`. Without it the page
    // still renders — the list links here as read navigation — but the fields
    // are read-only and Save/Delete are gone (spec 2026-09-04-02, "Gating").
    const canWrite = useCan('configuration.write')

    const [form, setForm] = useState<RelationTypeFormState | null>(null)
    const [pageError, setPageError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)
    const [confirmingDelete, setConfirmingDelete] = useState(false)
    const [pendingAggregating, setPendingAggregating] = useState<boolean | null>(null)

    const {
        data: relationType,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['relationType', orgCode, uid],
        enabled: !!uid,
        queryFn: async (): Promise<AssetRelationType> => {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/asset-relation-types/{uid}',
                { params: { path: { orgCode, uid } } }
            )
            if (response.error) {
                throw new Error(apiErrorMessage(response.error, 'Failed to load relation type'))
            }
            return response.data as AssetRelationType
        },
    })

    const { data: usage } = useQuery({
        queryKey: relationUsageQueryKey(orgCode, relationType?.code || ''),
        enabled: !!relationType?.code,
        queryFn: () => fetchRelationUsage(orgCode, relationType!.code),
    })

    // Seed / re-seed the editable copy whenever the server row changes (prop
    // → state sync, per front/CLAUDE.md's hooks rules).
    useEffect(() => {
        if (relationType) setForm(relationTypeToForm(relationType))
    }, [relationType])

    const displayName = relationType ? relationTypeLabel(relationType, locale) : ''

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('breadcrumb.home'), path: `/${orgCode}` },
                { title: t('breadcrumb.org_settings'), path: `/${orgCode}/settings` },
                { title: t('relation_types.title'), path: `/${orgCode}/relation-types` },
                { title: displayName || t('relation_types.title') },
            ],
            [orgCode, displayName, t]
        )
    )

    const saveMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.PATCH(
                '/api/v1/organizations/{orgCode}/asset-relation-types/{uid}',
                { params: { path: { orgCode, uid } }, body: formToPatchRequest(form!) }
            )
            if (response.error) {
                throw new Error(apiErrorMessage(response.error, 'Failed to save relation type'))
            }
            return response.data as AssetRelationType
        },
        onSuccess: () => {
            setNotice(t('relation_types.form.saved'))
            queryClient.invalidateQueries({ queryKey: ['relationType', orgCode, uid] })
            queryClient.invalidateQueries({ queryKey: relationTypesQueryKey(orgCode) })
        },
        onError: (e: Error) => setPageError(`${t('relation_types.form.save_error')} ${e.message}`),
    })

    const deleteMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.DELETE(
                '/api/v1/organizations/{orgCode}/asset-relation-types/{uid}',
                { params: { path: { orgCode, uid } } }
            )
            if (response.error) {
                // The backend answers 409 (ErrRelationTypeInUse) while active
                // relations still reference the type — surface it as the
                // actionable "remove them first" notice rather than a raw error.
                const status = response.response?.status
                if (status === 409) {
                    throw new Error('__IN_USE__')
                }
                throw new Error(apiErrorMessage(response.error, 'Failed to delete relation type'))
            }
            return true
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: relationTypesQueryKey(orgCode) })
            navigate({ to: '/$org/relation-types', params: { org: orgCode } })
        },
        onError: (e: Error) => {
            setConfirmingDelete(false)
            if (e.message === '__IN_USE__') {
                setPageError(
                    usage === undefined
                        ? t('relation_types.delete.in_use_unknown')
                        : t('relation_types.delete.in_use', { count: usage })
                )
                return
            }
            setPageError(`${t('relation_types.delete.error')} ${e.message}`)
        },
    })

    if (isLoading) {
        return (
            <div className="p-8 text-center text-muted-foreground" data-testid="relation-type-loading">
                {t('relation_types.loading')}
            </div>
        )
    }

    if (error || !relationType || !form) {
        return (
            <div className="p-8 text-center">
                <p className="text-destructive">{t('relation_types.not_found')}</p>
                <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => navigate({ to: '/$org/relation-types', params: { org: orgCode } })}
                    data-testid="relation-type-back-btn"
                >
                    {t('relation_types.back_to_list')}
                </Button>
            </div>
        )
    }

    const requestAggregatingChange = (next: boolean) => {
        if (!canWrite) return
        if (next === form.aggregating) return
        // Flipping the flag re-computes hierarchy rollups for every existing
        // relation of this type (asynchronously, through the job system), so
        // confirm explicitly whenever the type is actually in use.
        if (usage && usage > 0) {
            setPendingAggregating(next)
            return
        }
        setForm({ ...form, aggregating: next })
    }

    return (
        <div className="container mx-auto max-w-4xl py-8">
            <div className="rounded-lg bg-card shadow-lg">
                <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
                    <div className="min-w-0">
                        <h1 className="flex items-center gap-2 truncate text-2xl font-bold text-foreground">
                            {relationType.emoji && <span aria-hidden="true">{relationType.emoji}</span>}
                            {relationType.color && (
                                <span
                                    className="inline-block h-3 w-3 shrink-0 rounded-full border border-border"
                                    style={{ backgroundColor: relationType.color }}
                                />
                            )}
                            <span className="truncate">{displayName}</span>
                            {relationType.aggregating && (
                                <Badge
                                    variant="secondary"
                                    title={t('relation_types.aggregating_tooltip')}
                                    data-testid="relation-type-aggregating-badge"
                                >
                                    <AppIcon path={mdiSigma} size="xs" className="mr-1" />
                                    {t('relation_types.aggregating_badge')}
                                </Badge>
                            )}
                        </h1>
                        <p className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                            <code className="rounded bg-muted px-2 py-0.5 text-xs">{relationType.code}</code>
                            <span data-testid="relation-type-usage">
                                {usage === undefined
                                    ? t('relation_types.usage_unknown')
                                    : t('relation_types.usage_count', { count: usage })}
                            </span>
                        </p>
                    </div>
                    {canWrite && (
                        <div className="flex shrink-0 items-center gap-2">
                            <Button
                                onClick={() => {
                                    setPageError(null)
                                    setNotice(null)
                                    saveMutation.mutate()
                                }}
                                disabled={saveMutation.isPending}
                                size="sm"
                                data-testid="relation-type-save-btn"
                            >
                                {saveMutation.isPending
                                    ? t('relation_types.form.saving')
                                    : t('relation_types.form.save')}
                            </Button>
                            <Button
                                variant="destructive"
                                size="icon"
                                aria-label={t('relation_types.delete.button')}
                                title={t('relation_types.delete.button')}
                                onClick={() => {
                                    setPageError(null)
                                    setNotice(null)
                                    setConfirmingDelete(true)
                                }}
                                data-testid="relation-type-delete-btn"
                            >
                                <AppIcon path={mdiDelete} size="md" />
                            </Button>
                        </div>
                    )}
                </div>

                <div className="space-y-6 p-6">
                    {pageError && (
                        <Alert variant="destructive" data-testid="relation-type-page-error">
                            <AlertDescription>{pageError}</AlertDescription>
                        </Alert>
                    )}
                    {notice && (
                        <Alert data-testid="relation-type-notice">
                            <AlertDescription>{notice}</AlertDescription>
                        </Alert>
                    )}

                    <RelationTypeFormFields
                        org={orgCode}
                        value={form}
                        // A read-only caller still sees every stored value; the
                        // edits it could physically type are dropped instead of
                        // living in a form it has no way to submit.
                        onChange={next => {
                            if (canWrite) setForm(next)
                        }}
                        codeEditable={false}
                        requestAggregatingChange={requestAggregatingChange}
                        disabled={saveMutation.isPending || !canWrite}
                    />
                </div>
            </div>

            {/* Aggregating flip confirmation — only shown when relations exist. */}
            <AlertDialog
                open={pendingAggregating !== null}
                onOpenChange={open => {
                    if (!open) setPendingAggregating(null)
                }}
            >
                <AlertDialogContent data-testid="relation-type-aggregating-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('relation_types.aggregating_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('relation_types.aggregating_dialog.description', { count: usage ?? 0 })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="relation-type-aggregating-cancel">
                            {t('relation_types.aggregating_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (pendingAggregating !== null) {
                                    setForm({ ...form, aggregating: pendingAggregating })
                                }
                                setPendingAggregating(null)
                            }}
                            data-testid="relation-type-aggregating-confirm"
                        >
                            {t('relation_types.aggregating_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Deletion confirmation. */}
            <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
                <AlertDialogContent data-testid="relation-type-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('relation_types.delete.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('relation_types.delete.description', { name: displayName })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="relation-type-delete-cancel">
                            {t('relation_types.delete.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={e => {
                                // Keep the dialog mounted until the request
                                // settles so an in-use rejection can close it
                                // itself and surface the notice underneath.
                                e.preventDefault()
                                deleteMutation.mutate()
                            }}
                            data-testid="relation-type-delete-confirm"
                        >
                            {deleteMutation.isPending
                                ? t('relation_types.delete.deleting')
                                : t('relation_types.delete.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
